import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Upload, Download, Trash2, Plus, FileText, CheckCircle2, Circle, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

function MessagesTab({ clientId }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const endRef = useRef(null);
  const load = useCallback(() => api.get(`/portal-admin/clients/${clientId}/messages`).then((r) => setMessages(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [messages]);
  const send = async (e) => {
    e.preventDefault(); if (!body.trim()) return;
    try { const { data } = await api.post(`/portal-admin/clients/${clientId}/messages`, { body }); setMessages((m) => [...m, data]); setBody(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-col h-[420px]">
      <div className="flex-1 overflow-y-auto space-y-2.5 p-1" data-testid="admin-messages-thread">
        {messages.length === 0 ? <p className="text-center text-[#71717A] py-8 text-sm">No messages yet.</p> :
          messages.map((m) => (
            <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === "admin" ? "fv-gold-gradient text-[#0A0A0B]" : "bg-[#1C1C1F] text-[#D4D4D8] border border-white/8"}`}>{m.body}</div>
            </div>
          ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply to your client…" data-testid="admin-message-input" className="bg-[#0d0d0f] border-white/10 text-white" />
        <Button type="submit" disabled={!body.trim()} data-testid="admin-message-send" className="fv-gold-gradient text-[#0A0A0B] rounded-lg shrink-0"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}

function DocumentsTab({ clientId }) {
  const [docs, setDocs] = useState([]);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get(`/portal-admin/clients/${clientId}/documents`).then((r) => setDocs(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/portal-admin/clients/${clientId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Shared with client"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (d) => {
    try { const { data } = await api.get(`/portal-admin/clients/${clientId}/documents/${d.doc_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data])); const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click(); window.URL.revokeObjectURL(url); }
    catch { toast.error("Could not download"); }
  };
  const remove = async (id) => { setDocs((x) => x.filter((d) => d.doc_id !== id)); try { await api.delete(`/portal-admin/clients/${clientId}/documents/${id}`); } catch { load(); } };
  return (
    <div>
      <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="admin-doc-input" />
      <Button onClick={() => fileRef.current?.click()} data-testid="admin-doc-upload" className="fv-gold-gradient text-[#0A0A0B] rounded-full mb-4"><Upload className="w-4 h-4 mr-1" /> Share document</Button>
      <div className="space-y-2 max-h-[380px] overflow-y-auto">
        {docs.length === 0 ? <p className="text-[#71717A] text-sm py-6 text-center">No documents.</p> :
          docs.map((d) => (
            <div key={d.doc_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-3">
              <div className="flex items-center gap-2.5 min-w-0"><FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <div className="min-w-0"><div className="text-white text-sm truncate">{d.filename}</div><div className="text-xs text-[#71717A]">{d.uploaded_by === "client" ? "From client" : "Shared by you"} · {fmtDate(d.created_at)}</div></div></div>
              <div className="flex gap-2 shrink-0"><button onClick={() => download(d)} className="text-[#A1A1AA] hover:text-white"><Download className="w-4 h-4" /></button>
                <button onClick={() => remove(d.doc_id)} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
            </div>
          ))}
      </div>
    </div>
  );
}

function ProgressTab({ clientId }) {
  const [milestones, setMilestones] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(() => api.get(`/portal-admin/clients/${clientId}/progress`).then((r) => setMilestones(r.data.milestones)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  const save = async (next) => { try { const { data } = await api.put(`/portal-admin/clients/${clientId}/progress`, { milestones: next }); setMilestones(data.milestones); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const toggle = (id) => save(milestones.map((m) => m.id === id ? { ...m, done: !m.done } : m));
  const add = (e) => { e.preventDefault(); if (!title.trim()) return; save([...milestones, { title, description: "", done: false }]); setTitle(""); };
  const remove = (id) => save(milestones.filter((m) => m.id !== id));
  return (
    <div>
      <div className="space-y-2 max-h-[340px] overflow-y-auto mb-4">
        {milestones.map((m) => (
          <div key={m.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#0d0d0f] p-3">
            <button onClick={() => toggle(m.id)} data-testid={`admin-milestone-toggle-${m.id}`}>{m.done ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Circle className="w-5 h-5 text-[#52525B]" />}</button>
            <div className="flex-1 min-w-0"><div className="text-white text-sm">{m.title}</div>{m.description && <div className="text-xs text-[#71717A]">{m.description}</div>}</div>
            <button onClick={() => remove(m.id)} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a milestone…" data-testid="admin-milestone-input" className="bg-[#0d0d0f] border-white/10 text-white" />
        <Button type="submit" data-testid="admin-milestone-add" className="fv-gold-gradient text-[#0A0A0B] rounded-lg shrink-0"><Plus className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}

function InvoicesTab({ clientId }) {
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ description: "", amount: "" });
  const load = useCallback(() => api.get(`/portal-admin/clients/${clientId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post(`/portal-admin/clients/${clientId}/invoices`, { description: form.description, amount: parseFloat(form.amount) }); toast.success("Invoice sent"); setForm({ description: "", amount: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const voidInv = async (id) => { try { await api.post(`/portal-admin/clients/${clientId}/invoices/${id}/void`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-[1fr,120px,auto] gap-2 mb-4" data-testid="admin-invoice-form">
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" required data-testid="admin-invoice-desc" className="bg-[#0d0d0f] border-white/10 text-white" />
        <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount" required data-testid="admin-invoice-amount" className="bg-[#0d0d0f] border-white/10 text-white" />
        <Button type="submit" data-testid="admin-invoice-create" className="fv-gold-gradient text-[#0A0A0B] rounded-lg"><Plus className="w-4 h-4" /></Button>
      </form>
      <div className="space-y-2 max-h-[340px] overflow-y-auto">
        {invoices.length === 0 ? <p className="text-[#71717A] text-sm py-6 text-center flex flex-col items-center gap-2"><Receipt className="w-6 h-6" /> No invoices yet.</p> :
          invoices.map((inv) => (
            <div key={inv.invoice_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0d0d0f] p-3">
              <div className="min-w-0"><div className="text-white text-sm truncate">{inv.number} — {inv.description}</div><div className="text-xs text-[#71717A]">${Number(inv.amount).toFixed(2)} · {fmtDate(inv.created_at)}</div></div>
              <div className="flex items-center gap-2 shrink-0"><span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#D4D4D8] capitalize">{inv.status}</span>
                {inv.status !== "paid" && inv.status !== "void" && <button onClick={() => voidInv(inv.invoice_id)} className="text-xs text-[#71717A] hover:text-red-400">Void</button>}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

export default function ClientDetail({ clientId }) {
  return (
    <Tabs defaultValue="messages" className="w-full">
      <TabsList className="bg-[#0d0d0f] border border-white/8">
        <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
        <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
        <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
      </TabsList>
      <TabsContent value="messages" className="mt-4"><MessagesTab clientId={clientId} /></TabsContent>
      <TabsContent value="documents" className="mt-4"><DocumentsTab clientId={clientId} /></TabsContent>
      <TabsContent value="progress" className="mt-4"><ProgressTab clientId={clientId} /></TabsContent>
      <TabsContent value="invoices" className="mt-4"><InvoicesTab clientId={clientId} /></TabsContent>
    </Tabs>
  );
}

export { fmtDate };
